import React, { useState } from "react";
import { makeStyles } from "@mui/styles";
import { Alert, CircularProgress } from "@mui/material";
import { Close } from "@mui/icons-material";
import validator from "validator";
import { Api } from "../../../common/service/Api";
import { Button, TextInput, SelectMenuStandard } from "@truevault/aquilo";
import { UserRole } from "../../../common/service/server/Types";
import { Form } from "react-final-form";

const useClasses = makeStyles({
  inviteItem: {
    marginBottom: "14px",
  },
  inviteItemInner: {
    position: "relative",
    paddingLeft: "40px",
    marginTop: "-38px",
  },
  closeIcon: {
    position: "absolute",
    right: "-25px",
    top: "11px",
    cursor: "pointer",
  },
});

type InviteRecord = {
  firstName?: string;
  lastName?: string;
  email?: string;
  role?: UserRole;
  uniqueKey?: string;
  showErrors?: boolean;
};

const makeUniqueKey = () => `key_${Date.now()}_${Math.floor(Math.random() * 5000)}`;

export function WelcomeOverlay({
  organizationId,
  skipInvitations,
  onClose,
  alreadyViewedWelcomeScreen,
}: {
  onClose: () => void;
  organizationId: string;
  skipInvitations: boolean;
  alreadyViewedWelcomeScreen: boolean;
}) {
  const [showInvitations, setShowInvitations] = useState<boolean>(false);

  return showInvitations ? (
    <InviteUsersAquilo organizationId={organizationId} onDone={onClose} />
  ) : (
    <WelcomeOverlayAquilo
      onGetStartedClick={() => {
        if (skipInvitations === true || alreadyViewedWelcomeScreen) {
          onClose();
        } else {
          setShowInvitations(true);
        }
      }}
      alreadyViewed={alreadyViewedWelcomeScreen}
    />
  );
}

export function WelcomeOverlayAquilo({
  onGetStartedClick,
  alreadyViewed,
}: {
  onGetStartedClick: () => void;
  alreadyViewed: boolean;
}) {
  return (
    <div className="tw-boundary">
      {/* Don't use `flex-col` tailwind style since it conflicts with the `flex-col` style in flex.scss */}
      <div className="flex items-center mx-auto pt-[150px]" style={{ flexDirection: "column" }}>
        <img
          src="/assets/images/nav-icon-truevault.png"
          alt="TrueVault Logo"
          className="w-[86px] h-[86px]"
        />
        <div className="mt-[12px] text-primary-800 font-black text-[48px]">
          {alreadyViewed ? "Welcome back to TrueVault" : "Welcome to TrueVault"}
        </div>
        <div className="mt-[12px] text-primary-800 font-medium text-[20px]">
          {alreadyViewed
            ? "We will guide you through the steps to create your own:"
            : "Click 'Get Started' to create your own:"}
        </div>
        <div className="mt-[24px] flex items-center gap-[24px]">
          <div
            className="p-[16px] w-[174px] h-[118px] bg-primary-100 shadow-[0px_2px_12px_0px_rgba(0,0,0,0.25)] rounded-[8px] flex items-center"
            style={{ flexDirection: "column" }}
          >
            <img
              src="/assets/images/get_compliant/welcome_shield.svg"
              alt="Welcome Shield"
              className="w-[50px] h-[50px]"
            />
            <div className="mt-[6px] text-primary-600 font-medium text-[20px] text-center">
              Privacy Center
            </div>
          </div>
          <div
            className="p-[16px] w-[174px] h-[118px] bg-primary-100 shadow-[0px_2px_12px_0px_rgba(0,0,0,0.25)] rounded-[8px] flex items-center"
            style={{ flexDirection: "column" }}
          >
            <img
              src="/assets/images/get_compliant/welcome_map.svg"
              alt="Welcome Shield"
              className="w-[50px] h-[50px]"
            />
            <div className="mt-[6px] text-primary-600 font-medium text-[20px] text-center">
              Data Map
            </div>
          </div>
          <div
            className="p-[16px] w-[174px] h-[118px] bg-primary-100 shadow-[0px_2px_12px_0px_rgba(0,0,0,0.25)] rounded-[8px] flex items-center"
            style={{ flexDirection: "column" }}
          >
            <img
              src="/assets/images/get_compliant/welcome_note.svg"
              alt="Welcome Shield"
              className="w-[50px] h-[50px]"
            />
            <div className="mt-[6px] text-primary-600 font-medium text-[20px] text-center">
              Privacy Policy
            </div>
          </div>
        </div>
        <div className="mt-[36px]">
          <Button color="primary" onClick={onGetStartedClick}>
            {alreadyViewed ? "Continue" : "Get Started"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function InviteUsersAquilo({
  organizationId,
  onDone,
}: {
  organizationId: string;
  onDone: () => void;
}) {
  const classes = useClasses();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [invites, setInvites] = useState<InviteRecord[]>([
    {
      firstName: "",
      lastName: "",
      email: "",
      role: "GENERAL",
      showErrors: false,
      uniqueKey: makeUniqueKey(),
    },
  ]);

  const submitValues = async () => {
    setError(false);
    const errors = invites.some((invite) => {
      return !invite.firstName || !invite.lastName || !validator.isEmail(invite.email);
    });
    if (errors) {
      setInvites([
        ...invites.map((invite) => ({
          ...invite,
          showErrors: true,
        })),
      ]);
      return;
    }
    setLoading(true);
    try {
      await Promise.all(
        invites.map((invite) =>
          Api.organizationUser.inviteUser(organizationId, {
            firstName: invite.firstName,
            lastName: invite.lastName,
            email: invite.email,
            role: invite.role,
          }),
        ),
      );
      await Api.user.acknowledgeWelcome();
      onDone();
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="tw-boundary">
        <div className="flex items-center mx-auto pt-[50px]" style={{ flexDirection: "column" }}>
          <img
            src="/assets/images/nav-icon-truevault.png"
            alt="TrueVault Logo"
            className="w-[40px] h-[40px]"
          />
          <div className="mt-[12px] text-primary-800 font-bold text-[30px]">
            Invite your teammates
          </div>
          <div className="mt-[12px] max-w-[60ch] text-primary-800 font-medium text-[20px] text-center">
            Multiple people may handle customer and employee data, such as HR representatives. You
            can add users from settings any time.
          </div>
        </div>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "start",
          maxWidth: "900px",
          margin: "19px auto 0 auto",
          borderRadius: "24px",
          border: "1px solid #D1CED9",
          backgroundColor: "#FFFFFF",
          padding: "36px",
          boxShadow: "0 2px 12px 0 rgba(0, 0, 0, 0.12)",
        }}
      >
        {error && <Alert severity="error">Error sending invites, please try again later.</Alert>}
        <ol className="custom pl-0">
          {invites.map((invite, idx) => (
            <li className={classes.inviteItem} key={`invite-${invite.uniqueKey}`}>
              <div className={`${classes.inviteItemInner} fill`}>
                {/* Note: the wrapper <Form> from react-final-form is required for <TextInput> to render */}
                <Form
                  onSubmit={() => {}}
                  render={() => (
                    <div className="tw-boundary">
                      <div className="flex">
                        <div className="flex-[2] mr-[12px]">
                          <TextInput
                            field="firstName"
                            placeholder="John"
                            inputBorder={
                              invite.showErrors && invite.firstName?.length == 0
                                ? "border-red-500"
                                : undefined
                            }
                            onChange={(event) => {
                              setInvites([
                                ...invites.slice(0, idx),
                                {
                                  ...invite,
                                  showErrors: false,
                                  firstName: event.target.value,
                                },
                                ...invites.slice(idx + 1),
                              ]);
                            }}
                          />
                        </div>
                        <div className="flex-[2] mr-[12px]">
                          <TextInput
                            field="lastName"
                            placeholder="Smith"
                            inputBorder={
                              invite.showErrors && invite.lastName?.length == 0
                                ? "border-red-500"
                                : undefined
                            }
                            onChange={(event) => {
                              setInvites([
                                ...invites.slice(0, idx),
                                {
                                  ...invite,
                                  showErrors: false,
                                  lastName: event.target.value,
                                },
                                ...invites.slice(idx + 1),
                              ]);
                            }}
                          />
                        </div>
                        <div className="flex-[3] mr-[12px]">
                          <TextInput
                            field="email"
                            type="email"
                            placeholder="johnsmith@acme.com"
                            inputBorder={
                              invite.showErrors && !validator.isEmail(invite.email)
                                ? "border-red-500"
                                : undefined
                            }
                            autoComplete="off"
                            onChange={(event) => {
                              setInvites([
                                ...invites.slice(0, idx),
                                {
                                  ...invite,
                                  showErrors: false,
                                  email: event.target.value,
                                },
                                ...invites.slice(idx + 1),
                              ]);
                            }}
                          />
                        </div>
                        <div className="flex-[1.5]">
                          <SelectMenuStandard
                            items={[
                              { label: "General", value: "GENERAL" },
                              { label: "Admin", value: "ADMIN" },
                              { label: "HR", value: "HR" },
                            ]}
                            selectedValue={invite.role}
                            onSelect={(value) => {
                              setInvites([
                                ...invites.slice(0, idx),
                                {
                                  ...invite,
                                  role: value as UserRole,
                                },
                                ...invites.slice(idx + 1),
                              ]);
                            }}
                            height="38px"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                />
                {idx > 0 && (
                  <div
                    className={`${classes.closeIcon} color-neutral-600 text-t2`}
                    onClick={() => {
                      setInvites([...invites.slice(0, idx), ...invites.slice(idx + 1)]);
                    }}
                  >
                    <Close className="text-t3" />
                  </div>
                )}
              </div>
            </li>
          ))}
        </ol>
        {invites.length < 5 && (
          <div className="tw-boundary">
            <div
              className="text-primary-600 text-[14px] font-medium underline cursor-pointer pl-[38px]"
              onClick={() => {
                setInvites([
                  ...invites,
                  {
                    firstName: "",
                    lastName: "",
                    email: "",
                    role: "GENERAL",
                    showErrors: false,
                    uniqueKey: makeUniqueKey(),
                  },
                ]);
              }}
            >
              Add another email
            </div>
          </div>
        )}
      </div>
      <div
        className="tw-boundary"
        style={{
          marginTop: "24px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div>
          <Button
            color="primary"
            onClick={() => {
              submitValues();
            }}
          >
            {loading ? <CircularProgress size={20} color="secondary" /> : "Send Invites"}
          </Button>
        </div>
        <div>
          <div
            className="text-primary-600 text-[14px] font-medium underline cursor-pointer mt-[8px]"
            onClick={async () => {
              await Api.user.acknowledgeWelcome();
              onDone();
            }}
          >
            Skip
          </div>
        </div>
      </div>
    </>
  );
}
